import { useMemo } from 'react'

import type { CustomField } from '@sales-center/common'

import { customValuesToFormModel } from '@user/components/CustomFields'
import type { DraftLead } from '@user/store/draftLeads/typings'
import { pestNamesToIds } from '@user/store/targetPests'
import type { ITargetPest } from '@user/store/targetPests'

import type { UpdateRequiredFieldsFormModel } from '../typings'

/**
 * Keys outside the flat model that the reused Add Lead selects and the assignment rule search
 * read. `leadSourceId`, `targetPests` and `state` are kept in step with `sourceId`,
 * `targetPestIds` and `stateAbbreviation` by the field components.
 */
export type AutoReviewFormValues = UpdateRequiredFieldsFormModel & {
  leadSourceId: string
  targetPests: string[]
  state: string
  type: string
}

const toId = (value: string | number | null | undefined) =>
  Number(value) || null

/**
 * Seeds the form from the draft's current values, including the context (branch, postal code,
 * campaign, ...) that the rule search and team filtering read, so a prefilled value still counts.
 */
export function useInitialValues(
  lead: DraftLead,
  customFields: CustomField[],
  allTargetPests: ITargetPest[],
) {
  return useMemo<AutoReviewFormValues>(() => {
    const targetPestIds = pestNamesToIds(allTargetPests, lead.targetPests)
    return {
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      company: lead.company || '',
      phoneNumber: lead.phoneNumber || '',
      emailAddress: lead.emailAddress || '',
      branchId: lead.branchId || '',
      salesTeamId: toId(lead.salesTeamId),
      assigneeId: lead.assigneeId || '',
      salesFunnelId: toId(lead.salesFunnelId),
      salesFunnelStageId: toId(lead.salesFunnelStageId),
      divisionId: lead.divisionId || '',
      altPhoneNumber: lead.altPhoneNumber || '',
      street1: lead.street1 || '',
      street2: lead.street2 || '',
      city: lead.city || '',
      stateAbbreviation: lead.stateAbbreviation || '',
      postalCode: lead.postalCode || '',
      locationTypeId: lead.locationTypeId || '',
      note: lead.note || '',
      targetPestIds,
      campaignId: lead.campaignId || '',
      sourceId: lead.sourceId || '',
      referralCode: lead.referralCode || '',
      referringBillTo: lead.referringBillTo || '',
      customFields: customValuesToFormModel(
        customFields,
        lead.customFieldValues ?? [],
      ),
      leadSourceId: lead.sourceId || '',
      targetPests: targetPestIds,
      state: lead.stateAbbreviation || '',
      type: lead.type || 'Manual',
    }
  }, [lead, customFields, allTargetPests])
}
